import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import { useQuery } from '@tanstack/react-query';
import { Cohort, cohortsApi } from '../../api/cohorts';
import { MentorInfo } from '../../api/mentors';
import { ProgramAdmin } from '../../api/programs';
import { EnrollmentModal } from '../../components/EnrollmentModal';
import { MentorRequestModal } from '../../components/MentorRequestModal';
import { WithdrawModal } from '../../components/WithdrawModal';
import { PersonChip } from '../../components/PersonChip';
import { UserLink } from '../../components/UserLink';
import { SlackIcon } from '../../components/SlackIcon';
import { GlobeIcon } from '../../components/GlobeIcon';
import { queryKeys } from '../../lib/queryClient';

interface ParticipantData {
  progressPercent: number;
  completedCount: number;
  totalCount: number;
  mentor: MentorInfo | null;
  pendingApprovals: number;
  onWithdraw?: () => Promise<void>;
}

export interface CohortOverviewTabProps {
  cohort: Cohort;
  programAdmins: ProgramAdmin[];
  programWikiUrl?: string;
  programOwnerId?: string;
  programOwnerName?: string;
  // Visitor CTAs
  onEnroll?: () => Promise<void>;
  onRequestMentor?: (notes: string) => Promise<void>;
  // Participant progress
  participantData?: ParticipantData;
  onNavigateToTab?: (tabId: string) => void;
}

export function CohortOverviewTab({
  cohort,
  programAdmins,
  programWikiUrl,
  programOwnerId,
  programOwnerName,
  onEnroll,
  onRequestMentor,
  participantData,
  onNavigateToTab,
}: CohortOverviewTabProps) {
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showMentorRequestModal, setShowMentorRequestModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  // Action items — disabled only when the cohort is both completed and closed.
  const { data: actionItems } = useQuery({
    queryKey: queryKeys.cohorts.actionItems(cohort.cohortId),
    queryFn: () => cohortsApi.getActionItems(cohort.cohortId),
    staleTime: 60 * 1000, // 1 minute — action items change frequently
    enabled: !(cohort.status === 'completed' && cohort.isOpen === false),
  });

  // canEnroll and canRequestMentor come from the cohort object (computed server-side)
  const canEnroll = cohort.canEnroll ?? false;
  const canRequestMentor = cohort.canRequestMentor ?? false;

  return (
    <>
      {/* Modals */}
      {onEnroll && (
        <EnrollmentModal
          visible={showEnrollmentModal}
          cohortName={cohort.cohortName}
          cohortDescription={cohort.description}
          startDate={cohort.startDate}
          endDate={cohort.endDate}
          onDismiss={() => setShowEnrollmentModal(false)}
          onConfirm={onEnroll}
        />
      )}
      {onRequestMentor && (
        <MentorRequestModal
          visible={showMentorRequestModal}
          cohortName={cohort.cohortName}
          onDismiss={() => setShowMentorRequestModal(false)}
          onSubmit={onRequestMentor}
        />
      )}
      {participantData?.onWithdraw && (
        <WithdrawModal
          visible={showWithdrawModal}
          cohortName={cohort.cohortName}
          onDismiss={() => setShowWithdrawModal(false)}
          onConfirm={participantData.onWithdraw}
        />
      )}

      <SpaceBetween size="l">

        {/* ── Cohort Details ──────────────────────────────────────────────── */}
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <>
                  <SpaceBetween direction="horizontal" size="xs">
                    {canEnroll && onEnroll && (
                      <Button variant="primary" onClick={() => setShowEnrollmentModal(true)}>
                        Enroll Now
                      </Button>
                    )}
                    {canRequestMentor && onRequestMentor && (
                      <Button onClick={() => setShowMentorRequestModal(true)}>
                        Request to be Mentor
                      </Button>
                    )}
                    {participantData?.onWithdraw && (
                      <Button onClick={() => setShowWithdrawModal(true)}>
                        Withdraw from Cohort
                      </Button>
                    )}
                  </SpaceBetween>
                </>
              }
            >
              Cohort Details
            </Header>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {cohort.description && <Box variant="p">{cohort.description}</Box>}

            {/* Wiki and Slack links */}
            {(programWikiUrl || cohort.slackChannel) && (
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {programWikiUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <GlobeIcon size={16} />
                    <Link external href={programWikiUrl}>Wiki</Link>
                  </div>
                )}
                {cohort.slackChannel && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <SlackIcon size={16} />
                    <Link external href={cohort.slackChannel}>Slack Channel</Link>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Dates */}
              <div style={{ display: 'flex', gap: '24px' }}>
                {cohort.registrationStartDate && cohort.registrationEndDate && (
                  <Box>
                    <Box variant="awsui-key-label">Registration</Box>
                    {new Date(cohort.registrationStartDate).toLocaleDateString()} –{' '}
                    {new Date(cohort.registrationEndDate).toLocaleDateString()}
                  </Box>
                )}
                <Box>
                  <Box variant="awsui-key-label">Cohort Dates</Box>
                  {new Date(cohort.startDate).toLocaleDateString()} –{' '}
                  {new Date(cohort.endDate).toLocaleDateString()}
                </Box>
              </div>

              {/* Org restriction */}
              {cohort.visibility === 'org-restricted' && cohort.orgLeader && (
                <div>
                  <Box variant="awsui-key-label">Org restriction</Box>
                  <PersonChip
                    login={cohort.orgLeader}
                    name={cohort.orgLeaderName ?? cohort.orgLeader}
                    marginTop="4px"
                  />
                </div>
              )}

              {/* Program Owner */}
              {programOwnerId && (
                <div>
                  <Box variant="awsui-key-label">Program Owner</Box>
                  <div style={{ marginTop: '4px' }}>
                    <PersonChip login={programOwnerId} name={programOwnerName ?? programOwnerId} />
                  </div>
                </div>
              )}

              {/* Program admins */}
              <div>
                <Box variant="awsui-key-label">Program Admins</Box>
                {programAdmins.length === 0 ? (
                  <Box color="text-body-secondary">No program admins found</Box>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                    {[...programAdmins].sort((a, b) => a.name.localeCompare(b.name)).map((admin) => (
                      <PersonChip key={admin.userId} login={admin.userId} name={admin.name} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>

        {/* ── Participant: Your Progress ──────────────────────────────────── */}
        {participantData && (
          <Container header={<Header variant="h2">Your Progress</Header>}>
            <SpaceBetween size="m">
              <ProgressBar
                value={participantData.progressPercent}
                label={`${participantData.completedCount} of ${participantData.totalCount} activities completed`}
                status={participantData.progressPercent === 100 ? 'success' : 'in-progress'}
                resultText={participantData.progressPercent === 100 ? 'All activities complete' : undefined}
              />
              {cohort.mentorsEnabled && (
                <div>
                  <Box variant="awsui-key-label">Your Mentor</Box>
                  <Box margin={{ top: 'xs' }}>
                    {participantData.mentor ? (
                      <UserLink
                        userName={participantData.mentor.mentorId}
                        userId={participantData.mentor.mentorId}
                      />
                    ) : (
                      <Box color="text-body-secondary">Not yet assigned</Box>
                    )}
                  </Box>
                </div>
              )}
            </SpaceBetween>
          </Container>
        )}

        {/* ── Action Items — driven by backend action-items endpoint ──────── */}
        {actionItems && actionItems.role !== 'none' && !(
          actionItems.role === 'participant' &&
          (actionItems.pendingApprovals ?? 0) === 0 &&
          (actionItems.overdueActivities ?? 0) === 0 &&
          (actionItems.dueThisWeekActivities ?? 0) === 0
        ) && (
          <Container header={<Header variant="h2">Action Items</Header>}>
            {actionItems.role === 'admin' && (
              (actionItems.pendingMentorRequests ?? 0) === 0 &&
              (actionItems.pendingGraduations ?? 0) === 0 &&
              (actionItems.unassignedParticipants ?? 0) === 0 ? (
                <Box color="text-body-secondary">No action items at this time</Box>
              ) : (
                <SpaceBetween size="s">
                  {(actionItems.pendingMentorRequests ?? 0) > 0 && (
                    <Alert type="warning" action={<Button onClick={() => onNavigateToTab?.('mentors')}>Review</Button>}>
                      <strong>{actionItems.pendingMentorRequests}</strong> mentor{' '}
                      {actionItems.pendingMentorRequests === 1 ? 'request' : 'requests'} awaiting approval
                    </Alert>
                  )}
                  {(actionItems.unassignedParticipants ?? 0) > 0 && (
                    <Alert type="warning" action={<Button onClick={() => onNavigateToTab?.('mentors')}>Assign</Button>}>
                      <strong>{actionItems.unassignedParticipants}</strong>{' '}
                      {actionItems.unassignedParticipants === 1 ? 'participant needs' : 'participants need'} a mentor assigned
                    </Alert>
                  )}
                  {(actionItems.pendingGraduations ?? 0) > 0 && (
                    <Alert type="warning" action={<Button onClick={() => onNavigateToTab?.('graduation')}>Review</Button>}>
                      <strong>{actionItems.pendingGraduations}</strong> graduation{' '}
                      {actionItems.pendingGraduations === 1 ? 'request' : 'requests'} awaiting approval
                    </Alert>
                  )}
                </SpaceBetween>
              )
            )}

            {actionItems.role === 'mentor' && (
              (actionItems.menteeCount ?? 0) === 0 ? (
                <Box color="text-body-secondary">You don't have any mentees assigned yet</Box>
              ) : (actionItems.pendingActivityReviews ?? 0) === 0 ? (
                <Box color="text-body-secondary">
                  No action items — all caught up with your {actionItems.menteeCount}{' '}
                  {actionItems.menteeCount === 1 ? 'mentee' : 'mentees'}
                </Box>
              ) : (
                <Alert type="warning" action={<Button onClick={() => onNavigateToTab?.('my-mentees')}>Review</Button>}>
                  <strong>{actionItems.pendingActivityReviews}</strong>{' '}
                  {actionItems.pendingActivityReviews === 1 ? 'activity' : 'activities'} from your mentees awaiting review
                </Alert>
              )
            )}

            {actionItems.role === 'participant' && (actionItems.pendingApprovals ?? 0) > 0 && (
              <Alert type="info" action={<Button onClick={() => onNavigateToTab?.('my-activities')}>View</Button>}>
                <strong>{actionItems.pendingApprovals}</strong>{' '}
                {actionItems.pendingApprovals === 1 ? 'activity is' : 'activities are'} awaiting mentor approval
              </Alert>
            )}
            {actionItems.role === 'participant' && (actionItems.overdueActivities ?? 0) > 0 && (
              <Alert type="error" action={<Button onClick={() => onNavigateToTab?.('my-activities')}>View</Button>}>
                <strong>{actionItems.overdueActivities}</strong>{' '}
                {actionItems.overdueActivities === 1 ? 'activity is' : 'activities are'} overdue
              </Alert>
            )}
            {actionItems.role === 'participant' && (actionItems.dueThisWeekActivities ?? 0) > 0 && (
              <Alert type="warning" action={<Button onClick={() => onNavigateToTab?.('my-activities')}>View</Button>}>
                <strong>{actionItems.dueThisWeekActivities}</strong>{' '}
                {actionItems.dueThisWeekActivities === 1 ? 'activity is' : 'activities are'} due this week
              </Alert>
            )}
          </Container>
        )}

      </SpaceBetween>
    </>
  );
}
